
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import uuid from 'uuid';
import * as echarts from 'echarts';
import SecondaryLineBarChartFilterable from '@/components/reports-v3/components/filterables/SecondaryLineBarChartFilterable.vue';
import moment from 'moment';
import SelectFilter from '@/components/reports-v3/components/filters/SelectFilter.vue';
import { COL, getTableName, sqlExtractYear, sqlExtractMonth, sqlSumByDocType, sqlSum } from '@/worker/db/sqlHelpers';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { PermissionsGroup, ValueText } from '@/store/models.def';
import { GobiColor } from '@/helpers/color';
import gdbx from '@/store/modules/gdbx';
import { DataViewFormatter } from '@/components/reports-v3/components/elements/charts/helpers/dataViewFormatter';
import { TooltipFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tooltipFormatter';
import DataInfo from '@/components/reports-v3/components/elements/DataInfo.vue';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
	components: {
		SecondaryLineBarChartFilterable,
		SelectFilter,
		DataInfo,
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
		SlPhModuleOnlyToggle,
	},
})
export default class CustomerSalesTrendLineChart extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['sales', 'customers'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = [];

	public xAxisData: string[] = [];
	public series: echarts.ECharts[] | echarts.ECharts | any = [];

	public selectedColumn: string = this.columnList[0].value;
	public selectedLine: string = this.lineList[0].value;
	public selectedComparedYear: any = 1; // 1 year back by default
	public columnDatas: number[] = [];
	public comparedColumnDatas: number[] = [];
	public accumulatedLineResults: number[] = [];
	public accumulatedComparedLineResults: number[] = [];

	// Cached raw data - avoid refetching when only column/line changes
	private _cachedData: {
		// SL data this year
		slIvCsAmounts: number[];
		slDnAmounts: number[];
		slCnAmounts: number[];
		slIvCsProfits: number[];
		slDnProfits: number[];
		slCnProfits: number[];
		// AR data this year
		arIvAmounts: number[];
		arCnAmounts: number[];
		arDnAmounts: number[];
		// SL data compared year
		slIvCsAmountsLY: number[];
		slDnAmountsLY: number[];
		slCnAmountsLY: number[];
		slIvCsProfitsLY: number[];
		slDnProfitsLY: number[];
		slCnProfitsLY: number[];
		// AR data compared year
		arIvAmountsLY: number[];
		arCnAmountsLY: number[];
		arDnAmountsLY: number[];
		// Payment
		paymentAmounts: number[];
		paymentAmountsLY: number[];
		// Outstanding data
		ivdnAmounts: number[];
		koAmounts: number[];
		gainLossAmounts: number[];
		unappliedAmounts: number[];
		ivdnAmountsLY: number[];
		koAmountsLY: number[];
		gainLossAmountsLY: number[];
		unappliedAmountsLY: number[];
	} | null = null;
	private _cachedDataKey: string = '';

	/**
	 * Key for data fetching - only changes when filters change (not column/line)
	 */
	private get _dataFetchKey(): string {
		return JSON.stringify([
			this.computedYearRange,
			this.compareYearRange,
			this.selectedAgents,
			this.selectedCustomers,
		]);
	}

	public get yAxisLabel2() {
		let label: string = '';

		if (this.selectedLine === 'salesMargin') {
			label = '%';
		}

		if (
			this.selectedLine === 'cummulativeSales' ||
			this.selectedLine === 'cummulativeProfit' ||
			this.selectedLine === 'outstanding' ||
      this.selectedLine === 'payment'
		) {
			label = this.currencySymbol;
		}
		return label;
	}

	public get compareYearList(): ValueText[] {
		const list: ValueText[] = [];

		// Show years back with end month label (MMM YYYY format)
		for (let i = 1; i < 10; i++) {
			const endDate = moment(this.computedYearRange[1]).add(-i, 'year');
			list.push({
				text: endDate.format('MMM YYYY'),
				value: i,
			});
		}

		list.unshift({ text: 'None', value: 'None' });

		return list;
	}

	public get selectedLineName() {
		return this.lineList.find((item) => item.value === this.selectedLine)!.text;
	}

	public get columnList(): ValueText[] {
		const list = [
			{ text: 'Sales', value: 'sales' },
			{ text: 'Sales Profit', value: 'salesProfit' },
			{ text: 'Payment', value: 'payment' },
			{ text: 'Credit Note', value: 'creditNote' },
		];

		return list;
	}

	public get lineList(): ValueText[] {
		const list = [
			{ text: 'None', value: 'none' },
			{ text: 'Outstanding', value: 'outstanding' },
			{ text: 'Sales Margin (Percentage)', value: 'salesMargin' },
        // remove cumulative
			{ text: 'Sales', value: 'cummulativeSales' },
        // remove cumulative
			{ text: 'Profit', value: 'cummulativeProfit' },
        // remove cumulative
			{ text: 'Credit Note', value: 'cummulativeCreditNote' },
      { text: 'Payment', value: 'payment' },
		];

		return list;
	}

	public get currentYear() {
		return moment(this.selectedAsOfDate).year();
	}

	public get currentMonth() {
		return moment(this.selectedAsOfDate).month();
	}

	/**
	 * Rolling 12-month range ending at selected month
	 * Example: If selected month is Oct 2025, range = Nov 2024 - Oct 2025
	 */
	public get computedYearRange(): [number, number] {
		return [
			moment(this.selectedDateRange[1])
				.add(-1, 'year')
				.add(1, 'month')
				.startOf('month')
				.valueOf(),
			moment(this.selectedDateRange[1]).valueOf(),
		];
	}

	/**
	 * Compare year range: same 12-month period, shifted by N years
	 * Example: If this year is Nov 2024 - Oct 2025 and compare = 1 year ago,
	 * then compare range = Nov 2023 - Oct 2024
	 */
	public get compareYearRange(): [number, number] {
		const yearsBack = this.selectedComparedYear === 'None' ? 0 : this.selectedComparedYear;
		return [
			moment(this.computedYearRange[0])
				.add(-yearsBack, 'year')
				.valueOf(),
			moment(this.computedYearRange[1])
				.add(-yearsBack, 'year')
				.endOf('month')
				.valueOf(),
		];
	}

	public get currencySymbol() {
		return gdbx.currencySymbol;
	}

	public get dateRangeLabel(): string {
		const thisYearLabel = `${moment(this.computedYearRange[0]).format('DD MMM YYYY')} - ${moment(this.computedYearRange[1]).format('DD MMM YYYY')}`;
		if (this.selectedComparedYear === 'None') {
			return thisYearLabel;
		}
		const comparedYearLabel = `${moment(this.compareYearRange[0]).format('DD MMM YYYY')} - ${moment(this.compareYearRange[1]).format('DD MMM YYYY')}`;
		return `${thisYearLabel} vs ${comparedYearLabel}`;
	}


	public get decimal() {
		return gdbx.numDecimal;
	}

	public get dataViewFormatter() {
		return DataViewFormatter.salesTrackerWithShare;
	}

	// Per-series totals so the tooltip can show each month's share of its own series' year total.
	// Only bar series get a total (> 0); cumulative line series get 0, which suppresses their %.
	public get tooltipFormatter() {
		const seriesTotals = (this.series || []).map((s: any) => {
			if (!s || s.type !== 'bar' || !Array.isArray(s.data)) {
				return 0;
			}
			return s.data.reduce((acc: number, v: any) => acc + Math.abs(Number(v) || 0), 0);
		});
		return TooltipFormatter.basicWithSeriesShare(seriesTotals);
	}

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

	public get slPhModuleOnly() {
		return settingx.now.slPhModuleOnly;
	}

	public get expensiveHook() {
		const {
			selectedDateRange,
			selectedCustomers,
			selectedAgents,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
			selectedColumn,
			selectedLine,
			selectedComparedYear,
		} = this;
		return JSON.stringify([
			selectedDateRange,
			selectedCustomers,
			selectedAgents,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
			selectedColumn,
			selectedLine,
			selectedComparedYear,
		]);
	}

	public async expensiveCalc() {
		const jobId = (this.currentJobId = uuid.v4());
		this.$forceUpdate();
		this.generateLoadingText(0.2, 0.4);

		const monthCount = 12;
		const currentFetchKey = this._dataFetchKey;
		const useCache = this._cachedData !== null && this._cachedDataKey === currentFetchKey;

		// ========================================
		// Check if we can use cached data (filters unchanged)
		// ========================================
		let slIvCsAmounts: number[];
		let slDnAmounts: number[];
		let slCnAmounts: number[];
		let slIvCsProfits: number[];
		let slDnProfits: number[];
		let slCnProfits: number[];
		let arIvAmounts: number[];
		let arCnAmounts: number[];
		let arDnAmounts: number[];
		let slIvCsAmountsLY: number[];
		let slDnAmountsLY: number[];
		let slCnAmountsLY: number[];
		let slIvCsProfitsLY: number[];
		let slDnProfitsLY: number[];
		let slCnProfitsLY: number[];
		let arIvAmountsLY: number[];
		let arCnAmountsLY: number[];
		let arDnAmountsLY: number[];
		let paymentAmounts: number[];
		let paymentAmountsLY: number[];
		let ivdnAmounts: number[];
		let koAmounts: number[];
		let gainLossAmounts: number[];
		let unappliedAmounts: number[];
		let ivdnAmountsLY: number[];
		let koAmountsLY: number[];
		let gainLossAmountsLY: number[];
		let unappliedAmountsLY: number[];

		if (useCache) {
			slIvCsAmounts = this._cachedData!.slIvCsAmounts;
			slDnAmounts = this._cachedData!.slDnAmounts;
			slCnAmounts = this._cachedData!.slCnAmounts;
			slIvCsProfits = this._cachedData!.slIvCsProfits;
			slDnProfits = this._cachedData!.slDnProfits;
			slCnProfits = this._cachedData!.slCnProfits;
			arIvAmounts = this._cachedData!.arIvAmounts;
			arCnAmounts = this._cachedData!.arCnAmounts;
			arDnAmounts = this._cachedData!.arDnAmounts;
			slIvCsAmountsLY = this._cachedData!.slIvCsAmountsLY;
			slDnAmountsLY = this._cachedData!.slDnAmountsLY;
			slCnAmountsLY = this._cachedData!.slCnAmountsLY;
			slIvCsProfitsLY = this._cachedData!.slIvCsProfitsLY;
			slDnProfitsLY = this._cachedData!.slDnProfitsLY;
			slCnProfitsLY = this._cachedData!.slCnProfitsLY;
			arIvAmountsLY = this._cachedData!.arIvAmountsLY;
			arCnAmountsLY = this._cachedData!.arCnAmountsLY;
			arDnAmountsLY = this._cachedData!.arDnAmountsLY;
			paymentAmounts = this._cachedData!.paymentAmounts;
			paymentAmountsLY = this._cachedData!.paymentAmountsLY;
			ivdnAmounts = this._cachedData!.ivdnAmounts;
			koAmounts = this._cachedData!.koAmounts;
			gainLossAmounts = this._cachedData!.gainLossAmounts;
			unappliedAmounts = this._cachedData!.unappliedAmounts;
			ivdnAmountsLY = this._cachedData!.ivdnAmountsLY;
			koAmountsLY = this._cachedData!.koAmountsLY;
			gainLossAmountsLY = this._cachedData!.gainLossAmountsLY;
			unappliedAmountsLY = this._cachedData!.unappliedAmountsLY;
		} else {
			// ========================================
			// FETCH NEW DATA using rawQuery in parallel
			// ========================================
			const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
			let customerFilter = '';
			if (this.selectedCustomers.length > 0) {
				const list = this.selectedCustomers.map((c) => `'${escapeStr(c)}'`).join(',');
				customerFilter = ` AND "CODE" IN (${list})`;
			}
			let agentFilter = '';
			if (this.selectedAgents.length > 0) {
				const list = this.selectedAgents.map((a) => `'${escapeStr(a)}'`).join(',');
				agentFilter = ` AND "AGENT" IN (${list})`;
			}
			const filters = customerFilter + agentFilter;

			const queryHead = await this._ensureTablesLoaded([
				'sales', 'creditNotes', 'customerTrades', 'customerIvDns', 'customerKos',
			] as any[]);
			this.checkInterrupted(jobId);

			const [
				salesDataTY, creditNotesTY, customerTradesTY,
				salesDataLY, creditNotesLY, customerTradesLY,
				paymentDataTY, paymentDataLY,
				ivdnDataTY, koDataTY, tradeDataTY,
				ivdnDataLY, koDataLY, tradeDataLY,
			] = await Promise.all([
				this._loadSalesDataByMonth(queryHead, this.computedYearRange, filters),
				this._loadCreditNotesDataByMonth(queryHead, this.computedYearRange, filters),
				this._loadCustomerTradesDataByMonth(queryHead, this.computedYearRange, filters),
				this._loadSalesDataByMonth(queryHead, this.compareYearRange, filters),
				this._loadCreditNotesDataByMonth(queryHead, this.compareYearRange, filters),
				this._loadCustomerTradesDataByMonth(queryHead, this.compareYearRange, filters),
				this._loadPaymentDataByMonth(queryHead, this.computedYearRange, filters),
				this._loadPaymentDataByMonth(queryHead, this.compareYearRange, filters),
				this._loadIvDnDataByMonth(queryHead, this.computedYearRange, filters),
				this._loadKoDataByMonth(queryHead, this.computedYearRange, filters),
				this._loadTradeUnappliedByMonth(queryHead, this.computedYearRange, filters),
				this._loadIvDnDataByMonth(queryHead, this.compareYearRange, filters),
				this._loadKoDataByMonth(queryHead, this.compareYearRange, filters),
				this._loadTradeUnappliedByMonth(queryHead, this.compareYearRange, filters),
			]);
			this.checkInterrupted(jobId);
			this.generateLoadingText(0.4, 0.95);

			const align = <T>(
				data: Array<{ year: number; month: number } & T>,
				range: [number, number],
				fn: (i: T) => number,
			) => this._alignToMonthArray(data, range, fn);

			slIvCsAmounts = align(salesDataTY, this.computedYearRange, (i) => i.ivCsAmount);
			slDnAmounts = align(salesDataTY, this.computedYearRange, (i) => i.dnAmount);
			slCnAmounts = align(creditNotesTY, this.computedYearRange, (i) => i.amount);
			slIvCsProfits = align(salesDataTY, this.computedYearRange, (i) => i.ivCsProfit);
			slDnProfits = align(salesDataTY, this.computedYearRange, (i) => i.dnProfit);
			slCnProfits = align(creditNotesTY, this.computedYearRange, (i) => i.profit);
			arIvAmounts = align(customerTradesTY, this.computedYearRange, (i) => i.ivAmount);
			arCnAmounts = align(customerTradesTY, this.computedYearRange, (i) => i.cnAmount);
			arDnAmounts = align(customerTradesTY, this.computedYearRange, (i) => i.dnAmount);

			slIvCsAmountsLY = align(salesDataLY, this.compareYearRange, (i) => i.ivCsAmount);
			slDnAmountsLY = align(salesDataLY, this.compareYearRange, (i) => i.dnAmount);
			slCnAmountsLY = align(creditNotesLY, this.compareYearRange, (i) => i.amount);
			slIvCsProfitsLY = align(salesDataLY, this.compareYearRange, (i) => i.ivCsProfit);
			slDnProfitsLY = align(salesDataLY, this.compareYearRange, (i) => i.dnProfit);
			slCnProfitsLY = align(creditNotesLY, this.compareYearRange, (i) => i.profit);
			arIvAmountsLY = align(customerTradesLY, this.compareYearRange, (i) => i.ivAmount);
			arCnAmountsLY = align(customerTradesLY, this.compareYearRange, (i) => i.cnAmount);
			arDnAmountsLY = align(customerTradesLY, this.compareYearRange, (i) => i.dnAmount);

			paymentAmounts = align(paymentDataTY, this.computedYearRange, (i) => i.amount);
			paymentAmountsLY = align(paymentDataLY, this.compareYearRange, (i) => i.amount);

			ivdnAmounts = align(ivdnDataTY, this.computedYearRange, (i) => i.amount);
			koAmounts = align(koDataTY, this.computedYearRange, (i) => i.amount);
			gainLossAmounts = align(koDataTY, this.computedYearRange, (i) => i.gainLoss);
			unappliedAmounts = align(tradeDataTY, this.computedYearRange, (i) => i.unappliedAmount);

			ivdnAmountsLY = align(ivdnDataLY, this.compareYearRange, (i) => i.amount);
			koAmountsLY = align(koDataLY, this.compareYearRange, (i) => i.amount);
			gainLossAmountsLY = align(koDataLY, this.compareYearRange, (i) => i.gainLoss);
			unappliedAmountsLY = align(tradeDataLY, this.compareYearRange, (i) => i.unappliedAmount);

			this._cachedData = {
				slIvCsAmounts, slDnAmounts, slCnAmounts,
				slIvCsProfits, slDnProfits, slCnProfits,
				arIvAmounts, arCnAmounts, arDnAmounts,
				slIvCsAmountsLY, slDnAmountsLY, slCnAmountsLY,
				slIvCsProfitsLY, slDnProfitsLY, slCnProfitsLY,
				arIvAmountsLY, arCnAmountsLY, arDnAmountsLY,
				paymentAmounts, paymentAmountsLY,
				ivdnAmounts, koAmounts, gainLossAmounts, unappliedAmounts,
				ivdnAmountsLY, koAmountsLY, gainLossAmountsLY, unappliedAmountsLY,
			};
			this._cachedDataKey = currentFetchKey;
		}

		// ========================================
		// Calculate amounts based on toggle settings (always runs)
		// ========================================
		const calc = (iv: number[], cn: number[], dn: number[]) =>
			iv.map((v, i) => v - (this.includeCreditNote ? cn[i] : 0) + (this.includeDebitNote ? dn[i] : 0));

		const slSalesAmt = calc(slIvCsAmounts, slCnAmounts, slDnAmounts);
		const slSalesAmtLY = calc(slIvCsAmountsLY, slCnAmountsLY, slDnAmountsLY);
		const arSalesAmt = calc(arIvAmounts, arCnAmounts, arDnAmounts);
		const arSalesAmtLY = calc(arIvAmountsLY, arCnAmountsLY, arDnAmountsLY);

		const slProfitAmt = calc(slIvCsProfits, slCnProfits, slDnProfits);
		const slProfitAmtLY = calc(slIvCsProfitsLY, slCnProfitsLY, slDnProfitsLY);
		const arProfitAmt = slProfitAmt.map((p, i) => p + (arSalesAmt[i] - slSalesAmt[i]));
		const arProfitAmtLY = slProfitAmtLY.map((p, i) => p + (arSalesAmtLY[i] - slSalesAmtLY[i]));

		const salesAmounts = this.slPhModuleOnly ? slSalesAmt : arSalesAmt;
		const salesAmountsCompared = this.slPhModuleOnly ? slSalesAmtLY : arSalesAmtLY;
		const profitAmounts = this.slPhModuleOnly ? slProfitAmt : arProfitAmt;
		const profitAmountsCompared = this.slPhModuleOnly ? slProfitAmtLY : arProfitAmtLY;
		const cnAmounts = this.slPhModuleOnly ? slCnAmounts : arCnAmounts;
		const cnAmountsCompared = this.slPhModuleOnly ? slCnAmountsLY : arCnAmountsLY;

		let lineDatas: number[] = [];
		let comparedLineDatas: number[] = [];
		const accumulatedProfitDatas: number[] = [];
		const accumulatedSalesDatas: number[] = [];
		const accumulatedComparedProfitDatas: number[] = [];
		const accumulatedComparedSalesDatas: number[] = [];
		this.accumulatedLineResults = [];
		this.accumulatedComparedLineResults = [];

		switch (this.selectedColumn) {
			case 'sales':
				this.columnDatas = salesAmounts;
				this.comparedColumnDatas = salesAmountsCompared;
				break;
			case 'salesProfit':
				this.columnDatas = profitAmounts;
				this.comparedColumnDatas = profitAmountsCompared;
				break;
			case 'payment':
				this.columnDatas = paymentAmounts;
				this.comparedColumnDatas = paymentAmountsLY;
				break;
			case 'creditNote':
				this.columnDatas = cnAmounts;
				this.comparedColumnDatas = cnAmountsCompared;
				break;
		}

		switch (this.selectedLine) {
			case 'outstanding':
				lineDatas = ivdnAmounts.map((v, i) => v - koAmounts[i] - gainLossAmounts[i] - unappliedAmounts[i]);
				comparedLineDatas = ivdnAmountsLY.map((v, i) => v - koAmountsLY[i] - gainLossAmountsLY[i] - unappliedAmountsLY[i]);
				break;
			case 'salesMargin':
				for (let i = 0; i < monthCount; i++) {
					accumulatedProfitDatas[i] = profitAmounts[i] + (i > 0 ? accumulatedProfitDatas[i - 1] : 0);
					accumulatedSalesDatas[i] = salesAmounts[i] + (i > 0 ? accumulatedSalesDatas[i - 1] : 0);
					accumulatedComparedProfitDatas[i] = profitAmountsCompared[i] + (i > 0 ? accumulatedComparedProfitDatas[i - 1] : 0);
					accumulatedComparedSalesDatas[i] = salesAmountsCompared[i] + (i > 0 ? accumulatedComparedSalesDatas[i - 1] : 0);
				}
				break;
			case 'cummulativeSales':
				lineDatas = salesAmounts;
				comparedLineDatas = salesAmountsCompared;
				break;
			case 'cummulativeCreditNote':
				lineDatas = cnAmounts;
				comparedLineDatas = cnAmountsCompared;
				break;
			case 'cummulativeProfit':
				lineDatas = profitAmounts;
				comparedLineDatas = profitAmountsCompared;
				break;
			case 'payment':
				lineDatas = paymentAmounts;
				comparedLineDatas = paymentAmountsLY;
				break;
			case 'none':
				break;
		}

		for (let i = 0; i < monthCount; i++) {
			if (i === 0) {
				this.accumulatedComparedLineResults[i] = comparedLineDatas[i];
				this.accumulatedLineResults[i] = lineDatas[i];
			} else {
				this.accumulatedComparedLineResults[i] = comparedLineDatas[i] + this.accumulatedComparedLineResults[i - 1];
				this.accumulatedLineResults[i] = lineDatas[i] + this.accumulatedLineResults[i - 1];
			}
			if (this.selectedLine === 'salesMargin') {
				this.accumulatedLineResults[i] = (accumulatedProfitDatas[i] / accumulatedSalesDatas[i]) * 100;
				this.accumulatedComparedLineResults[i] =
					(accumulatedComparedProfitDatas[i] / accumulatedComparedSalesDatas[i]) * 100;
			}
		}

		this.generateLoadingText(1);

		// Series labels with date range
		const thisYearLabel = `${moment(this.computedYearRange[0]).format('MMM YY')} - ${moment(this.computedYearRange[1]).format('MMM YY')}`;
		const comparedYearLabel = this.selectedComparedYear !== 'None'
			? `${moment(this.compareYearRange[0]).format('MMM YY')} - ${moment(this.compareYearRange[1]).format('MMM YY')}`
			: '';

		// X-axis labels
		this.xAxisData = this._buildMonthLabels();

		const yearDatas: any = {
			name: this.selectedColumn[0].toUpperCase() + this.selectedColumn.substring(1) + ' ' + thisYearLabel,
			type: 'bar', barWidth: '30%', itemStyle: { color: GobiColor.COLOR1 }, data: this.columnDatas,
		};
		const comparedYearDatas: any = {
			name: this.selectedColumn[0].toUpperCase() + this.selectedColumn.substring(1) + ' ' + comparedYearLabel,
			type: 'bar', itemStyle: { color: GobiColor.COLOR3 }, barWidth: '30%', data: this.comparedColumnDatas,
		};
		const line: any = {
			name: this.selectedLineName + ' ' + thisYearLabel,
			type: 'line', itemStyle: { color: GobiColor.COLOR5 }, yAxisIndex: 1, data: this.accumulatedLineResults,
		};
		const comparedLine: any = {
			name: this.selectedLineName + ' ' + comparedYearLabel,
			type: 'line', itemStyle: { color: GobiColor.COLOR6 }, yAxisIndex: 1, data: this.accumulatedComparedLineResults,
		};

		this.series = [yearDatas];
		if (this.selectedComparedYear !== 'None') {
			this.series.push(comparedYearDatas);
		}
		if (this.selectedLine !== 'none') {
			this.series.push(line);
			if (this.selectedComparedYear !== 'None') {
				this.series.push(comparedLine);
			}
		}

		this.saveHistory('series', 'xAxisData');
	}

	public onHovering(value: boolean, filterId: string) {
		this.$emit('hovering', value, filterId);
	}
	public onEditing(value: boolean, filterId: string) {
		this.$emit('editing', value, filterId);
	}

	private _buildMonthLabels(): string[] {
		const labels: string[] = [];
		const start = moment(this.computedYearRange[0]);
		for (let i = 0; i < 12; i++) {
			labels.push(start.clone().add(i, 'month').format('MMM YY'));
		}
		return labels;
	}

	private async _loadSalesDataByMonth(queryHead: any, dateRange: [number, number], filters: string): Promise<Array<{
		year: number; month: number;
		ivCsAmount: number; dnAmount: number;
		ivCsProfit: number; dnProfit: number;
	}>> {
		const table = getTableName('sales');
		const results = await queryHead.rawQuery(`
			SELECT
				${sqlExtractYear(COL.DOCDATE)},
				${sqlExtractMonth(COL.DOCDATE)},
				${sqlSumByDocType(['IV', 'CS'], COL.AMOUNT, 'iv_cs_amount')},
				${sqlSumByDocType(['DN'], COL.AMOUNT, 'dn_amount')},
				${sqlSumByDocType(['IV', 'CS'], COL.PROFIT, 'iv_cs_profit')},
				${sqlSumByDocType(['DN'], COL.PROFIT, 'dn_profit')}
			FROM ${table}
			WHERE TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${dateRange[0]} AND ${dateRange[1]}
				${filters}
			GROUP BY year, month
			ORDER BY year, month
		`);
		return results.map((r: any) => ({
			year: Number(r.year), month: Number(r.month),
			ivCsAmount: Number(r.iv_cs_amount) || 0, dnAmount: Number(r.dn_amount) || 0,
			ivCsProfit: Number(r.iv_cs_profit) || 0, dnProfit: Number(r.dn_profit) || 0,
		}));
	}

	private async _loadCreditNotesDataByMonth(
		queryHead: any, dateRange: [number, number], filters: string,
	): Promise<Array<{
		year: number; month: number; amount: number; profit: number;
	}>> {
		const table = getTableName('creditNotes');
		const results = await queryHead.rawQuery(`
			SELECT
				${sqlExtractYear(COL.DOCDATE)},
				${sqlExtractMonth(COL.DOCDATE)},
				${sqlSum(COL.AMOUNT, 'amount')},
				${sqlSum(COL.PROFIT, 'profit')}
			FROM ${table}
			WHERE TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${dateRange[0]} AND ${dateRange[1]}
				${filters}
			GROUP BY year, month
			ORDER BY year, month
		`);
		return results.map((r: any) => ({
			year: Number(r.year), month: Number(r.month),
			amount: Number(r.amount) || 0, profit: Number(r.profit) || 0,
		}));
	}

	private async _loadCustomerTradesDataByMonth(
		queryHead: any, dateRange: [number, number], filters: string,
	): Promise<Array<{
		year: number; month: number;
		ivAmount: number; cnAmount: number; dnAmount: number;
	}>> {
		const table = getTableName('customerTrades');
		const results = await queryHead.rawQuery(`
			SELECT
				${sqlExtractYear(COL.DOCDATE)},
				${sqlExtractMonth(COL.DOCDATE)},
				${sqlSumByDocType(['IV'], COL.LOCALAMOUNT, 'iv_amount')},
				${sqlSumByDocType(['CN'], COL.LOCALAMOUNT, 'cn_amount')},
				${sqlSumByDocType(['DN'], COL.LOCALAMOUNT, 'dn_amount')}
			FROM ${table}
			WHERE TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${dateRange[0]} AND ${dateRange[1]}
				${filters}
			GROUP BY year, month
			ORDER BY year, month
		`);
		return results.map((r: any) => ({
			year: Number(r.year), month: Number(r.month),
			ivAmount: Number(r.iv_amount) || 0, cnAmount: Number(r.cn_amount) || 0, dnAmount: Number(r.dn_amount) || 0,
		}));
	}

	private async _loadPaymentDataByMonth(queryHead: any, dateRange: [number, number], filters: string): Promise<Array<{
		year: number; month: number; amount: number;
	}>> {
		const table = getTableName('customerTrades');
		const results = await queryHead.rawQuery(`
			SELECT
				${sqlExtractYear(COL.DOCDATE)},
				${sqlExtractMonth(COL.DOCDATE)},
				${sqlSum(COL.LOCALAMOUNT, 'amount')}
			FROM ${table}
			WHERE TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${dateRange[0]} AND ${dateRange[1]}
				AND "DOCTYPE" = 'PM'
				${filters}
			GROUP BY year, month
			ORDER BY year, month
		`);
		return results.map((r: any) => ({
			year: Number(r.year), month: Number(r.month),
			amount: Number(r.amount) || 0,
		}));
	}

	private async _loadIvDnDataByMonth(queryHead: any, dateRange: [number, number], filters: string): Promise<Array<{
		year: number; month: number; amount: number;
	}>> {
		const table = getTableName('customerIvDns');
		const results = await queryHead.rawQuery(`
			SELECT
				${sqlExtractYear(COL.DOCDATE)},
				${sqlExtractMonth(COL.DOCDATE)},
				${sqlSum('"LOCALDOCAMT"', 'amount')}
			FROM ${table}
			WHERE TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${dateRange[0]} AND ${dateRange[1]}
				${filters}
			GROUP BY year, month
			ORDER BY year, month
		`);
		return results.map((r: any) => ({
			year: Number(r.year), month: Number(r.month),
			amount: Number(r.amount) || 0,
		}));
	}

	private async _loadKoDataByMonth(queryHead: any, dateRange: [number, number], filters: string): Promise<Array<{
		year: number; month: number; amount: number; gainLoss: number;
	}>> {
		const table = getTableName('customerKos');
		const results = await queryHead.rawQuery(`
			SELECT
				${sqlExtractYear('"FROMDOCDATE"')},
				${sqlExtractMonth('"FROMDOCDATE"')},
				${sqlSum('"KOLOCALAMT"', 'amount')},
				${sqlSum('"KOGAINLOSS"', 'gain_loss')}
			FROM ${table}
			WHERE TRY_CAST("TODOCDATE" AS BIGINT) BETWEEN ${dateRange[0]} AND ${dateRange[1]}
				AND TRY_CAST("FROMDOCDATE" AS BIGINT) BETWEEN ${dateRange[0]} AND ${dateRange[1]}
				${filters}
			GROUP BY year, month
			ORDER BY year, month
		`);
		return results.map((r: any) => ({
			year: Number(r.year), month: Number(r.month),
			amount: Number(r.amount) || 0, gainLoss: Number(r.gain_loss) || 0,
		}));
	}

	private async _loadTradeUnappliedByMonth(queryHead: any, dateRange: [number, number], filters: string): Promise<Array<{
		year: number; month: number; unappliedAmount: number;
	}>> {
		const table = getTableName('customerTrades');
		const results = await queryHead.rawQuery(`
			SELECT
				${sqlExtractYear(COL.DOCDATE)},
				${sqlExtractMonth(COL.DOCDATE)},
				${sqlSum('"UNAPPLIEDAMT"', 'unapplied_amount')}
			FROM ${table}
			WHERE TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${dateRange[0]} AND ${dateRange[1]}
				${filters}
			GROUP BY year, month
			ORDER BY year, month
		`);
		return results.map((r: any) => ({
			year: Number(r.year), month: Number(r.month),
			unappliedAmount: Number(r.unapplied_amount) || 0,
		}));
	}

	private _alignToMonthArray<T>(
		data: Array<{ year: number; month?: number } & T>,
		dateRange: [number, number],
		getValue: (item: T) => number,
	): number[] {
		const result = Array(12).fill(0);
		const startMonth = moment(dateRange[0]).month() + 1;
		const startYear = moment(dateRange[0]).year();
		for (const item of data) {
			const month = item.month || 1;
			const pos = (item.year - startYear) * 12 + (month - startMonth);
			if (pos >= 0 && pos < 12) {
				result[pos] = getValue(item as any);
			}
		}
		return result;
	}
}
